import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { InvoicingCutoffChartModel } from 'src/app/models/charts/invoicing/invoicing-cutoff-chart.model';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { environment } from 'src/environments/environment';
import { enpointCervinoWeb } from './../../configuration/enpointsCervino';
import { InvoicingCutoffModel } from './../../models/invoicing/invoicing-cutoff.model';
import { InvoicingRequestModel } from './../../models/invoicing/invoicing-request.model';

export interface StartJobResponse {
  jobId: string;
}

@Injectable({
  providedIn: 'root'
})
export class InvoicingCutoffService {

  constructor(private _httpClient: HttpClient) { }

  createInvoicingCutoff(invoicingCutoffContract: InvoicingCutoffModel): Observable<InvoicingCutoffModel> {
    return this._httpClient.post<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.CreateInvoicingCutoff}`,
      invoicingCutoffContract);
  }

  deleteCostCentersWithAlerts(invoicingCutoffId: number): Observable<void> {
    return this._httpClient.delete<void>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.DeleteCostCentersWithAlerts}/${invoicingCutoffId}`);
  }

  deleteInvoicingCutoff(invoicingCutoffId: number): Observable<InvoicingCutoffModel> {
    return this._httpClient.delete<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.DeleteInvoicingCutoff}/${invoicingCutoffId}`);
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.ExportGeneralConsultation}`, filters, {
      params, responseType: 'blob',
    });
  }

  exportPreInvoicingErrorsTxt(alerts: Array<PreInvoicingAlertModel>): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.ExportPreInvoicingErrorsTxt}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  getAllEnterprisesForSelectAsync(branchId: number): Observable<Array<CustomListDropdown>> {
    return this._httpClient.get<Array<CustomListDropdown>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetAllEnterprisesForSelectAsync}/${branchId}`);
  }

  getAllInvoicingCutoffsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetAllInvoicingCutoffsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetColumnsForGeneralConsultationAsync}`);
  }

  getConsecutiveInvoicingCutoff(invoicingCutoffId: number): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetConsecutiveInvoicingCutoff}/${invoicingCutoffId}`);
  }

  getFirstInvoicingCutoffByKeysAsync(branchId: number, invoicingCutoffClass: number, invoicingCutoffIdToUnlock: number): Observable<InvoicingCutoffModel> {
    const params = new HttpParams().set('branchId', branchId).set('invoicingCutoffClass', invoicingCutoffClass).set('invoicingCutoffIdToUnlock', invoicingCutoffIdToUnlock);
    return this._httpClient.get<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetFirstInvoicingCutoffByKeys}`,
      { params }
    );
  }

  getIndicatorsForGeneralConsultation(filters: ColumnResponseModel[]): Observable<InvoicingCutoffChartModel> {
    return this._httpClient.post<InvoicingCutoffChartModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetIndicatorsForGeneralConsultationAsync}`,
      filters
    );
  }

  getInitialAndFinalInvoiceByIdAsync(initialInvoice: number, finalInvoice: number): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetInitialAndFinalInvoiceByIdAsync}/${initialInvoice}/${finalInvoice}`);
  }

  getInitialAndFinalInvoiceByKeysAsync(branchId: number, consecutive: number): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetInitialAndFinalInvoiceByKeysAsync}/${branchId}/${consecutive}`);
  }

  getInvoicingCutoffById(invoicingCutoffId: number, invoicingCutoffIdToUnlock: number): Observable<InvoicingCutoffModel> {
    const params = new HttpParams().set('invoicingCutoffId', invoicingCutoffId.toString()).set('invoicingCutoffIdToUnlock', invoicingCutoffIdToUnlock.toString());
    return this._httpClient.get<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetInvoicingCutoffById}`,
      { params }
    );
  }

  getInvoicingCutoffByKeysAsync(branchId: number, consecutive: number, invoicingCutoffClass: number, invoicingCutoffIdToUnlock: number): Observable<InvoicingCutoffModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive).set('invoicingCutoffClass', invoicingCutoffClass).set('invoicingCutoffIdToUnlock', invoicingCutoffIdToUnlock);
    return this._httpClient.get<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetInvoicingCutoffByKeys}`,
      { params }
    );
  }

  getInvoicingCutoffByKeysForFormats(branchId: number, consecutive: number): Observable<InvoicingCutoffModel> {
    return this._httpClient.get<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetInvoicingCutoffByKeysForFormats}/${branchId}/${consecutive}`);
  }

  getLastInvoicingCutoffByKeysAsync(branchId: number, invoicingCutoffClass: number, invoicingCutoffIdToUnlock: number): Observable<InvoicingCutoffModel> {
    const params = new HttpParams().set('branchId', branchId).set('invoicingCutoffClass', invoicingCutoffClass).set('invoicingCutoffIdToUnlock', invoicingCutoffIdToUnlock);
    return this._httpClient.get<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetLastInvoicingCutoffByKeys}`,
      { params }
    );
  }

  getLastInvoicingCutoffByKeysForFormats(branchId: number): Observable<InvoicingCutoffModel> {
    return this._httpClient.get<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetLastInvoicingCutoffByKeysForFormats}/${branchId}`);
  }

  getNextInvoicingCutoffByKeysAsync(branchId: number, consecutive: number, invoicingCutoffClass: number, invoicingCutoffIdToUnlock: number): Observable<InvoicingCutoffModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive).set('invoicingCutoffClass', invoicingCutoffClass).set('invoicingCutoffIdToUnlock', invoicingCutoffIdToUnlock);
    return this._httpClient.get<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetNextInvoicingCutoffByKeys}`,
      { params }
    );
  }

  getPreviusInvoicingCutoffByKeysAsync(branchId: number, consecutive: number, invoicingCutoffClass: number, invoicingCutoffIdToUnlock: number): Observable<InvoicingCutoffModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive).set('invoicingCutoffClass', invoicingCutoffClass).set('invoicingCutoffIdToUnlock', invoicingCutoffIdToUnlock);
    return this._httpClient.get<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetPreviusInvoicingCutoffByKeys}`,
      { params }
    );
  }

  getPrintInvoicingCutoffForFormat(invoicingCutoffId: number): Observable<InvoicingCutoffModel> {
    return this._httpClient.get<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetPrintInvoicingCutoffForFormat}/${invoicingCutoffId}`);
  }

  getUserBlockByIdAsync(userBlockId: number): Observable<string> {
    return this._httpClient.get<{ value: string }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.GetUserBlockByIdAsync}/${userBlockId}`)
      .pipe(map(response => response.value));
  }

  openInvoicingCutoffById(invoicingCutoffId: number): Observable<InvoicingCutoffModel> {
    return this._httpClient.put<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.OpenInvoicingCutoffById}/${invoicingCutoffId}`,
      null);
  }

  preInvoicingByInvoicingCutoffId(invoicingCutoffId: number): Observable<StartJobResponse> {
    return this._httpClient.put<StartJobResponse>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.PreInvoicingByInvoicingCutoffId}/${invoicingCutoffId}`, null);
  }

  suspendInvoicingCutoffById(invoicingCutoffId: number): Observable<InvoicingCutoffModel> {
    return this._httpClient.put<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.SuspendInvoicingCutoffById}/${invoicingCutoffId}`,
      null);
  }

  toInvoiceInvoicingCutoffById(invoicingRequestModel: InvoicingRequestModel): Observable<InvoicingCutoffModel> {
    return this._httpClient.put<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.ToInvoiceInvoicingCutoffById}`,
      invoicingRequestModel);
  }

  unlockAllInvoicingCutoff(): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.UnlockAllInvoicingCutoff}`, null);
  }

  unlockInvoicingCutoffByBranchId(branchId: number): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.UnlockInvoicingCutoffByBranchId}/${branchId}`, null);
  }

  unlockInvoicingCutoffById(invoicingCutoffId: number): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.UnlockInvoicingCutoffById}/${invoicingCutoffId}`, null);
  }

  updateInvoicingCutoff(invoicingCutoffContract: InvoicingCutoffModel): Observable<InvoicingCutoffModel> {
    return this._httpClient.put<InvoicingCutoffModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.UpdateInvoicingCutoff}`,
      invoicingCutoffContract);
  }

  validateCountInvoicingCutoffsOpen(branchId: number): Observable<Array<number>> {
    return this._httpClient.get<Array<number>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoicingCutoff.ValidateCountInvoicingCutoffsOpen}/${branchId}`);
  }

}
